/**
 * POV projection classifier — "what KIND of image does this camera produce?"
 *
 * Pure module: no Cesium, no React, no DOM. This is deliberate. The 3D POV has
 * zero automated render coverage (scene3d/** is excluded from the vitest
 * coverage gate and Cesium is never mocked repo-wide), so the only way to test
 * the decision that drives the renderer is to keep that decision out of the
 * renderer.
 *
 * WHY THIS IS NOT `fovMode.isCircularFootprint`
 * ---------------------------------------------
 * `isCircularFootprint` answers a FOOTPRINT question: "does this camera paint a
 * full circle on the ground?" It returns true for `hfov >= 359.5` regardless of
 * body type. THIRTEEN catalog SKUs store `hfov: 360` on a `type: 'ptz'` body,
 * where 360 usually means the mechanical PAN RANGE, not a lens angle. Reusing
 * the footprint predicate here would render a mechanical PTZ as a fisheye
 * hemisphere. The PTZ test therefore runs BEFORE any hfov test (rule 2 below).
 *
 * The count is data, not a comment: `scripts/pov-census.json`, asserted by
 * `__tests__/povCensus.test.ts`. It said "eight" until 2026-07-29, by which
 * time the catalogue held thirteen. Of those thirteen, at least four are NOT
 * pan ranges and this rule misclassifies them: ACTi K9001 is a stitched strip
 * (11520x2700), Milesight MS-C2942-EPB is a 1.68 mm fisheye, and AXIS Q6020-E
 * and Q6300-E are multi-head panoramics. A better discriminator already exists
 * in `fovMode`: a strip aspect >= 1.9 or a sub-2 mm focal should veto the rule.
 * Tracked as Phase 5 of POV-FIDELITY-AUDIT-2026-07-29.md.
 *
 * WHY IT READS `model` AND NOT `name`
 * -----------------------------------
 * `Camera.name` is the USER'S label; every fanned multi-sensor head is literally
 * "Camera 5". The catalog SKU lives in `Camera.model` (placement writes
 * `model: model.name`, CCTVDesignerV2.tsx:3037). Any catalog-name heuristic fed
 * `Camera.name` can never fire, and would false-veto a camera the user happened
 * to name "Corner entrance".
 *
 * WHY IT CLASSIFIES AFTER THE MULTI-SENSOR FAN-OUT
 * -----------------------------------------------
 * Placement explodes a multi-sensor SKU into N sibling `Camera` rows, each
 * carrying its own head HFOV. The 26 Avigilon H5A/H6A-MH rows that declare
 * `hfov: 270` and AXIS P3727-PLE at 360 fan into heads at 96-97°, so the POV
 * never observes those catalog values. Classification runs on the PLACED camera.
 * The rows that genuinely survive the fan-out are the Dahua PSDW panorama heads
 * (`hfov: 360`, `8192x1800`), which are horizontal surrounds, not hemispheres.
 */
import type { Camera } from './types';
import { getDisplayedHfovDeg, getVfovDeg } from './lensMath';
import { isCeilingFisheyeModel, sensorAspect } from './fovMode';

/**
 * The widest HFOV a single Cesium `PerspectiveFrustum` can be asked to render.
 * 180° is a degenerate frustum (`tan(pi/2) = Infinity`) and crashes the engine,
 * so every POV render path clamps here.
 *
 * Single source of truth. Before this module the literal lived in four places
 * (povScene, export3dPdf twice, CameraPovView) with an in-code warning that they
 * had to stay in lockstep.
 */
export const POV_MAX_RENDER_HFOV_DEG = 175;

/**
 * Absolute ceiling for a frustum angle, used where the frustum is a SUPERSET of
 * the displayed field rather than the displayed field itself (the PDF export
 * widens the cone to contain both sensor axes, then crops back). Deliberately a
 * separate constant from `POV_MAX_RENDER_HFOV_DEG`: this one is only the
 * degenerate-geometry guard, not a statement about what the camera sees.
 */
export const POV_MAX_FRUSTUM_DEG = 178;

/** Below this the classifier never escalates: an ordinary directional lens. */
const WIDE_LENS_DEG = 170;

/**
 * Boundary between "one very wide lens" and "a multi-imager horizontal
 * surround". Empirical, not arbitrary: the catalog's only distinct HFOV values
 * in (200, 300) are 201, 209, 226 and 270, so 240 sits in an empty band and
 * cannot flip a real SKU under scrape jitter.
 */
const SURROUND_SPAN_DEG = 240;

/** Sensor aspect at or above which a wide lens is a stitched panoramic STRIP
 *  rather than a downward fisheye. Same constant the footprint classifier uses. */
const STRIP_ASPECT = 1.9;

/** A strip this wide is already a panorama even below the hemispheric threshold. */
const STRIP_MIN_SPAN_DEG = 150;

export type PovProjection =
  /** Ordinary perspective image. What every camera renders as today. */
  | 'rectilinear'
  /** Circular sensor image of a downward hemisphere (ceiling fisheye). */
  | 'fisheye-circular'
  /** Single wide strip: wall hemispheric lens or a stitched multi-imager. */
  | 'panorama-180'
  /** Horizontal surround produced by a multi-imager ring. */
  | 'panorama-360';

/** Which way the optical axis points, which decides how a renderer must sweep. */
export type PovAxis = 'forward' | 'nadir';

/** Which rule decided, so the UI can explain itself and tests can pin intent. */
export type PovProjectionSource =
  | 'override'
  | 'ptz-mechanical'
  | 'fisheye-mode-ceiling'
  | 'fisheye-mode-wall'
  | 'down-fisheye-optics'
  | 'surround-span'
  | 'stitched-strip'
  | 'wide-hemispheric'
  | 'default';

/** Everything the classifier reads. Deliberately a structural subset of Camera
 *  so callers can classify a catalog row or a placed camera with one function. */
export interface PovProjectionInput {
  hfov: number;
  type?: Camera['type'];
  resolution?: string;
  focalLength?: string;
  fisheyeMode?: Camera['fisheyeMode'];
  corridorMode?: boolean;
  /** CATALOG SKU string. On a placed camera that is `Camera.model`. NOT `name`. */
  model?: string;
}

export interface PovProjectionResolution {
  projection: PovProjection;
  /**
   * Azimuth the RENDERER must sweep to cover this image, degrees. For a nadir
   * dome that is 360 whatever the lens angle is, because the footprint is a
   * full circle on the ground. `povTiles` plans passes from this.
   *
   * NOT an optical angle. Read `lensHfovDeg` for that. The resolution panel
   * read this one as a field of view and therefore under-reported pixel
   * density on every ceiling fisheye by exactly `360 / hfov`: 2.00x on the 180
   * deg rows, up to 4.68x across the 427 catalogue rows that classify
   * `fisheye-circular` with hfov below 359.5.
   */
  spanDeg: number;
  /**
   * The camera's real horizontal field of view, degrees, corridor mode already
   * folded in. This is the number every density and DORI calculation wants.
   */
  lensHfovDeg: number;
  axis: PovAxis;
  source: PovProjectionSource;
  /** True when one rectilinear frustum cannot show this span honestly, i.e. the
   *  rendered picture is narrower than the camera's real image. Drives the
   *  caveat label; the renderer clamps regardless. */
  exceedsFrustum: boolean;
  /** True when `spanDeg` is a mechanical pan range rather than a lens angle
   *  (the eight `type:'ptz'` bodies storing hfov 360). Callers must not print it
   *  as a field of view. */
  spanIsPanRange: boolean;
}

/** Structural subset of a placed camera the classifier accepts directly. */
type ClassifiableCamera = Pick<
  Camera,
  'hfov' | 'type' | 'resolution' | 'focalLength' | 'fisheyeMode' | 'corridorMode' | 'model'
>;

function build(
  projection: PovProjection,
  spanDeg: number,
  axis: PovAxis,
  source: PovProjectionSource,
  lensHfovDeg: number,
  spanIsPanRange = false,
): PovProjectionResolution {
  return {
    projection,
    spanDeg,
    lensHfovDeg,
    axis,
    source,
    // A nadir hemisphere is unrenderable by a forward frustum whatever its span,
    // so it always carries the caveat.
    exceedsFrustum: axis === 'nadir' || (!spanIsPanRange && spanDeg > POV_MAX_RENDER_HFOV_DEG),
    spanIsPanRange,
  };
}

/**
 * Decide how a camera's POV should be projected.
 *
 * Precedence is load-bearing and every step earns its position:
 *   1. explicit user override, always wins
 *   2. mechanical PTZ, BEFORE any hfov test (the hfov-360 pan-range trap)
 *   3. explicit ceiling fisheye
 *   4. explicit wall fisheye, which SKIPS the downward-optics test entirely
 *   5. downward-fisheye optics heuristic, with the strip veto intact
 *   6. surround span
 *   7. stitched strip
 *   8. wide hemispheric lens
 *   9. ordinary lens
 */
export function resolvePovProjection(
  input: PovProjectionInput,
  override?: PovProjection,
): PovProjectionResolution {
  const rawHfov = Number.isFinite(input.hfov) ? input.hfov : 0;
  // The cone the rest of the app draws, so corridor mode is already folded in.
  const displayed = getDisplayedHfovDeg(rawHfov, input.resolution, input.corridorMode);
  const spanDeg = Math.max(0, Math.min(displayed, 360));
  const aspect = sensorAspect(input.resolution);
  const isStrip = aspect !== null && aspect >= STRIP_ASPECT;

  // 1. Override. Span still comes from the optics so a forced mode cannot invent
  //    coverage; only the projection changes.
  if (override) {
    const axis: PovAxis = override === 'fisheye-circular' ? 'nadir' : 'forward';
    return build(override, override === 'fisheye-circular' ? 360 : spanDeg, axis, 'override', displayed);
  }

  // 2. Mechanical PTZ. Runs before every hfov test: eight catalog bodies store
  //    hfov 360 as their PAN RANGE. A steerable head is always a normal lens.
  if (input.type === 'ptz') {
    const panRange = rawHfov >= 359.5;
    return build('rectilinear', spanDeg, 'forward', 'ptz-mechanical', displayed, panRange);
  }

  // 3. The user (or placement) said this is a ceiling dome. Trust it.
  if (input.fisheyeMode === 'ceiling') {
    return build('fisheye-circular', 360, 'nadir', 'fisheye-mode-ceiling', displayed);
  }

  // 4. Explicit wall mount. Skip the downward-optics heuristic below: the whole
  //    point of the flag is that this lens faces OUT, however fisheye it is.
  if (input.fisheyeMode === 'wall') {
    if (spanDeg >= WIDE_LENS_DEG || isStrip) {
      return build('panorama-180', spanDeg, 'forward', 'fisheye-mode-wall', displayed);
    }
    return build('rectilinear', spanDeg, 'forward', 'fisheye-mode-wall', displayed);
  }

  // 5. Downward-fisheye optics. Delegated to the SAME predicate that decides the
  //    2D footprint, so the POV and the coverage circle can never disagree about
  //    which cameras are hemispheres. Fed `model` because the predicate keys its
  //    curated allow/deny lists on catalog names.
  if (
    isCeilingFisheyeModel({
      hfov: rawHfov,
      type: input.type,
      focalLength: input.focalLength,
      resolution: input.resolution,
      name: input.model,
    })
  ) {
    return build('fisheye-circular', 360, 'nadir', 'down-fisheye-optics', displayed);
  }

  // 6. Horizontal surround. Reached only after the strip veto inside step 5 has
  //    already rejected these, which is exactly what saves the Dahua PSDW
  //    panorama heads (hfov 360 on an 8192x1800 sensor) from being called domes.
  if (spanDeg >= SURROUND_SPAN_DEG) {
    return build('panorama-360', spanDeg, 'forward', 'surround-span', displayed);
  }

  // 7. Stitched multi-imager strip.
  if (isStrip && spanDeg >= STRIP_MIN_SPAN_DEG) {
    return build('panorama-180', spanDeg, 'forward', 'stitched-strip', displayed);
  }

  // 8. A single hemispheric lens facing forward.
  if (spanDeg >= WIDE_LENS_DEG) {
    return build('panorama-180', spanDeg, 'forward', 'wide-hemispheric', displayed);
  }

  // 9. Everything else: 65,955 of the 66,968 catalogue rows, 98.5 percent.
  //    Counted by scripts/pov-census.mjs, asserted by povCensus.test.ts.
  return build('rectilinear', spanDeg, 'forward', 'default', displayed);
}

/** Convenience wrapper for a placed camera. */
export function resolveCameraPovProjection(
  camera: ClassifiableCamera,
  override?: PovProjection,
): PovProjectionResolution {
  return resolvePovProjection(
    {
      hfov: camera.hfov,
      type: camera.type,
      resolution: camera.resolution,
      focalLength: camera.focalLength,
      fisheyeMode: camera.fisheyeMode,
      corridorMode: camera.corridorMode,
      model: camera.model,
    },
    override,
  );
}

/** True when the POV subsystem needs to do anything beyond today's single
 *  rectilinear frustum. Lets every caller cheaply skip the whole feature for the
 *  ~98.6% of models that are ordinary lenses. */
export function needsPovDewarp(res: PovProjectionResolution): boolean {
  return res.projection !== 'rectilinear';
}

/** The HFOV a single Cesium frustum may actually be given for this camera.
 *  Clamped low so a sub-degree telephoto stays representable and high so a
 *  degenerate 180°+ frustum can never be constructed. */
export function povFrustumHfovDeg(res: PovProjectionResolution, minDeg: number): number {
  return Math.min(Math.max(res.spanDeg, minDeg), POV_MAX_RENDER_HFOV_DEG);
}

/**
 * Width/height the POV frame should use for this camera.
 *
 * The inset was hard-coded to 16:9. For a CORRIDOR-mode camera that is wrong
 * today and visibly so: corridor rotates the sensor 90°, `getDisplayedHfovDeg`
 * hands back the VERTICAL field as the horizontal one, and a landscape box then
 * frames the narrow axis across the screen. The PDF export already derives the
 * true portrait aspect and crops to it, so the inset and the delivered page
 * disagree for exactly these cameras.
 *
 * Clamped to a sane range so a corrupt resolution string cannot produce a
 * one-pixel-tall panel.
 */
/**
 * The camera's menu image-rotation, in degrees, with the historical default.
 *
 * Before this setting existed the app hard-coded "a quarter turn always comes
 * with the menu rotation, any other angle never does". That is a fair default
 * and it is what saved projects mean, so an unset field reproduces it exactly
 * and nothing changes for them.
 */
export function previewFlipDeg(
  camera: Pick<Camera, 'corridorMode'> & { previewFlipDeg?: number; rollDeg?: number },
): number {
  const f = camera.previewFlipDeg;
  if (f === 0 || f === 90 || f === 180 || f === 270) return f;
  // GUESSING IS LIMITED TO GENUINELY UNTOUCHED PROJECTS, and the narrowness is
  // the point. The old rule was "corridorMode implies a 90 menu rotation", which
  // is right for a file saved before this setting existed. But `corridorMode`
  // now also means "the mount passed a quarter turn", so the roll slider sets it
  // as a side effect: dragging to -46 decomposes to a quarter turn plus 44, the
  // guess fired, and the inset went portrait for someone who never touched the
  // menu. Reported twice on 2026-08-15, at 90 and then again at -46, because
  // pinning the value at the write site only closed the first path into it.
  //
  // A camera carrying `rollDeg` has been through the new control, so its menu
  // setting is a real choice and an absent one means zero. Only a file with the
  // old flag and no roll at all gets the legacy reading.
  const untouchedLegacy = camera.rollDeg === undefined;
  return camera.corridorMode && untouchedLegacy ? 90 : 0;
}

export function povFrameAspect(
  camera: Pick<Camera, 'resolution' | 'corridorMode'> & { previewFlipDeg?: number },
  opts: {
    /** True when the POV shows a cropped WINDOW into the image rather than the
     *  whole sensor frame (ePTZ). */
    windowed?: boolean;
    fallback?: number;
  } = {},
): number {
  const fallback = opts.fallback ?? 16 / 9;
  // A windowed view is its own frame: it is a normal rectilinear view cut out of
  // a wider image, exactly like the dewarped pane a VMS shows. Using the SENSOR
  // aspect here is the bug that put a 60-degree window inside a 4:1 letterbox on
  // a 360-degree camera, leaving a 16-degree vertical slit.
  if (opts.windowed) return fallback;
  // TWO SHAPES, and only two: 16:9 or 9:16.
  //
  // Which one is decided by the MENU ROTATION, not by how the housing hangs,
  // and that is the whole point. The sensor is landscape however it is bolted
  // up; a housing turned on its bracket still fills a 16:9 buffer, just with
  // the world lying on its side. Only the camera's own image rotation makes the
  // OUTPUT portrait. So a corridor mount with the menu left at 0 previews as
  // 16:9 with a sideways world — a real setup, and one you read by tilting your
  // head, which is exactly why installers turn the menu setting on.
  //
  // Deliberately independent of TILT. Nothing here ever read the tilt, so this
  // is not a change of behaviour but a promise now written down: the inset keeps
  // its shape at any aim, including near-nadir.
  //
  // ⚠️ The cost, stated rather than hidden: a body whose sensor is not 16:9 —
  // 1280x960 and the other 4:3 rows in the catalogue — is previewed in a frame
  // shaped unlike the picture it records. The COVERAGE maths is untouched and
  // still uses the real sensor aspect (`getVfovHalfRad`); only the inset's
  // outline is standardised.
  const quarterTurns = Math.round(previewFlipDeg(camera) / 90);
  return Math.abs(quarterTurns) % 2 === 1 ? 9 / 16 : 16 / 9;
}

/**
 * Width/height the RENDER must use, which is not the width/height the FRAME
 * shows. These are two different numbers and conflating them was a real defect.
 *
 * Cesium's frustum is strictly rectilinear and it derives the vertical angle
 * from the canvas: V = 2·atan(tan(H/2) / aspect). Feeding it the SENSOR aspect
 * therefore renders the pinhole vertical field, which is precisely the form
 * `getVfovHalfRad` was moved off because it OVERSTATES the vertical. Measured
 * on the shipping code: a 16:9 body at 90 deg rendered 58.7 deg of vertical
 * against the lens model's 49.7, at 106 deg it was 73.5 against 58.1, and at
 * 120 deg 88.5 against 65.3. Checked against a datasheet rather than against
 * ourselves: Hikvision DS-2CD2143G2-IU 2.8 mm publishes 102 x 55 deg, the lens
 * model gives 56.0, the pinhole form gives 69.5.
 *
 * So the preview showed a band of ground and sky above and below what the
 * device records, and disagreed with our own coverage polygon, which has used
 * the lens model all along. Reported as "widzę trochę rozjazd" (2026-08-13).
 *
 * The fix is to render at the aspect that reproduces the device's OWN pair of
 * angles, tan(H/2)/tan(V/2), and let the caller squeeze that render back into
 * the sensor-shaped frame. Nothing is cropped: both angles are exactly the
 * device's, and the only artefact is a uniform horizontal compression, which is
 * a second-order stand-in for the barrel compression a real wide lens applies
 * at its edges. The alternative, keeping the render at the sensor aspect and
 * cutting the horizontal to fit, throws away 8 to 11 deg per side on the most
 * common lenses in the catalogue and would put the preview at odds with the
 * coverage polygon in the other direction.
 *
 * A WINDOWED view is exempt and returns the frame aspect unchanged: a dewarped
 * ePTZ pane really is a rectilinear crop, so pinhole is the right model there.
 */
export function povRenderAspect(
  camera: Pick<Camera, 'resolution' | 'corridorMode' | 'hfov'> & { previewFlipDeg?: number; rollDeg?: number },
  frameAspect: number,
  opts: { windowed?: boolean } = {},
): number {
  if (opts.windowed) return frameAspect;
  // ⚠️ THE MENU DECIDES THE SHAPE, NOT THE BRACKET, exactly as in
  // `povFrameAspect` — and getting this line wrong is what kept the inset
  // flipping to portrait after both earlier fixes (reported three times on
  // 2026-08-15, last at -46). Passing `camera.corridorMode` here asked for the
  // axis-swapped pair whenever the MOUNT crossed a quarter turn, and since the
  // canvas is sized from this ratio the render went portrait however the frame
  // was shaped. The frame was never the thing on screen.
  const menuPortrait = Math.abs(Math.round(previewFlipDeg(camera) / 90)) % 2 === 1;
  const hDeg = getDisplayedHfovDeg(camera.hfov, camera.resolution, menuPortrait);
  const vDeg = getVfovDeg(camera.hfov, camera.resolution, menuPortrait);
  if (!Number.isFinite(hDeg) || !Number.isFinite(vDeg) || hDeg <= 0 || vDeg <= 0) return frameAspect;
  // Both half-angles have to stay inside a quarter turn or the tangent runs
  // away. A body that wide is classified panoramic and never reaches here, but
  // the guard costs nothing and a NaN aspect blanks the panel.
  const hHalf = Math.min((hDeg * Math.PI) / 360, 1.5533); // 89 deg
  const vHalf = Math.min((vDeg * Math.PI) / 360, 1.5533);
  const ratio = Math.tan(hHalf) / Math.tan(vHalf);
  if (!Number.isFinite(ratio) || ratio <= 0) return frameAspect;
  // Same clamp as the frame. When it bites, the flat render genuinely cannot
  // hold this camera's pair of angles and the caveat badge is what says so.
  return Math.min(Math.max(ratio, 0.4), 4);
}

/**
 * i18n key for the honest caveat shown next to the POV, or null when the render
 * is faithful.
 *
 * `renderedSpanDeg` is the angle the preview ACTUALLY draws. Pass it: the badge
 * is about the gap between what the device sees and what the screen shows, and
 * only the caller knows which path ran.
 *
 * It used to key off `exceedsFrustum`, which asks a different question ("would
 * one frustum be degenerate?") and therefore missed a 25 deg band. 15 catalogue
 * rows sit in it — the Reolink Duo family, the two-imager Uniview IPC2K24SE at
 * 160 deg, four Digital Watchdog bodies at 175 — all of which render as a 60 deg
 * ePTZ window with no badge at all, because `povWindowed` windows any
 * non-rectilinear body while `exceedsFrustum` only fires above 175. Counted by
 * scripts/pov-census.mjs as `panorama.belowTileGate`.
 */
export function povCaveatKey(
  res: PovProjectionResolution,
  renderedSpanDeg?: number,
): string | null {
  if (res.spanIsPanRange) return 'scene3d_pov_caveat_ptz_pan_range';
  const narrower =
    Number.isFinite(renderedSpanDeg) && (renderedSpanDeg as number) < res.spanDeg - 0.5;
  if (!res.exceedsFrustum && !narrower) return null;
  switch (res.projection) {
    case 'fisheye-circular':
      return 'scene3d_pov_caveat_fisheye';
    case 'panorama-360':
      return 'scene3d_pov_caveat_surround';
    case 'panorama-180':
      return 'scene3d_pov_caveat_panorama';
    default:
      return 'scene3d_pov_caveat_clamped';
  }
}
